package meineglasfaser.frontend.common.components.yaform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import meineglasfaser.api.routes.PrefetchedEntry
import meineglasfaser.api.ubl.UBLAmount
import meineglasfaser.frontend.common.components.widgets.Toast
import meineglasfaser.frontend.common.components.widgets.Toasts
import meineglasfaser.frontend.common.components.widgets.ariaBusyAndDisabled
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.pattern
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.readOnly
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.attributes.rows
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.AttrBuilderContext
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.ElementScope
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.NumberInput
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLFormElement
import kotlin.reflect.KProperty1

@DslMarker
annotation class YaFormDslScope

typealias YaFormProperty<T> = KProperty1<T, *>

class YaFormState<T : Any>(val formId: String, val initial: T?) {
    var data = mutableStateMapOf<String, Any>()
    var isBusy by mutableStateOf(false)
    var isLiveValidation by mutableStateOf(false)
}

@YaFormDslScope
class YaFormScope<T: Any>(
    base: ElementScope<HTMLFormElement>,
    val state: YaFormState<T>,
) : ElementScope<HTMLFormElement> by base {
    @Composable
    fun nameOf(prop: YaFormProperty<T>): String {
        return remember(prop.name) { "${state.formId}_${prop.name}" }
    }

    @Composable
    fun booleanValueOf(prop: YaFormProperty<T>): Boolean {
        return state.data.getOrPut(prop.name) {
            state.initial?.let { prop.get(it) } as? Boolean ?: false
        } as? Boolean ?: false
    }

    @Composable
    fun stringValueOf(prop: YaFormProperty<T>): String {
        return state.data.getOrPut(prop.name) {
            when (val raw = state.initial?.let { prop.get(it) }) {
                is UBLAmount -> raw.value.toPlainString()
                is String -> raw
                is Enum<*> -> raw.name
                else -> ""
            }
        } as? String ?: ""
    }

    @Composable
    fun localDateValueOf(prop: YaFormProperty<T>): String {
        return state.data.getOrPut(prop.name) {
            when (val raw = state.initial?.let { prop.get(it) }) {
                is LocalDate -> raw.toString()
                is String -> raw
                else -> ""
            }
        } as? String ?: ""
    }

    @Composable
    fun numberValueOf(prop: YaFormProperty<T>): Number {
        return state.data.getOrPut(prop.name) {
            when (val raw = state.initial?.let { prop.get(it) }) {
                is Number -> raw
                else -> 0
            }
        } as? Number ?: 0
    }

    @Suppress("UNCHECKED_CAST")
    @Composable
    inline fun <reified V> listValueOf(prop: YaFormProperty<T>): List<V> {
        return state.data.getOrPut(prop.name) {
            (state.initial?.let { prop.get(it) } as? List<*> ?: emptyList<V>())
                .filterIsInstance<V>()
        } as? List<V> ?: emptyList()
    }
}

class YaFormMap<T : Any>(val data: Map<String, Any>) {
    fun boolean(prop: YaFormProperty<T>): Boolean {
        return data[prop.name] as? Boolean ?: false
    }

    fun string(prop: YaFormProperty<T>): String {
        return data[prop.name]?.toString() ?: throw Exception("Pflichtfeld '${prop.name}' ist nicht gesetzt")
    }

    fun stringOrNull(prop: YaFormProperty<T>): String? {
        return data[prop.name]?.toString()?.ifBlank { null }
    }

    fun localDate(prop: YaFormProperty<T>): LocalDate {
        return when (val value = data[prop.name]) {
            is LocalDate -> value
            is String -> try {
                LocalDate.parse(value)
            } catch (_: Exception) {
                throw Exception("Pflichtfeld '${prop.name}' ist kein gültiges Datum")
            }
            else -> throw Exception("Pflichtfeld '${prop.name}' ist nicht gesetzt")
        }
    }

    fun localDateOrNull(prop: YaFormProperty<T>): LocalDate? {
        return when (val value = data[prop.name]) {
            is LocalDate -> value
            is String -> try { LocalDate.parse(value) } catch (_: Exception) { null }
            else -> null
        }
    }

    fun int(prop: YaFormProperty<T>): Int {
        return when (val value = data[prop.name]) {
            is Int -> value
            is String -> try {
                value.toInt()
            } catch (_: Exception) {
                throw Exception("Pflichtfeld '${prop.name}' ist keine gültige Zahl")
            }
            else -> throw Exception("Pflichtfeld '${prop.name}' ist nicht gesetzt")
        }
    }

    fun intOrNull(prop: YaFormProperty<T>): Int? {
        return when (val value = data[prop.name]) {
            is Int -> value
            is String -> value.toIntOrNull()
            else -> null
        }
    }

    fun double(prop: YaFormProperty<T>): Double {
        return when (val value = data[prop.name]) {
            is Double -> value
            is String -> try {
                value.toDouble()
            } catch (_: Exception) {
                throw Exception("Pflichtfeld '${prop.name}' ist keine gültige Zahl")
            }
            else -> throw Exception("Pflichtfeld '${prop.name}' ist nicht gesetzt")
        }
    }

    inline fun <reified V> list(prop: YaFormProperty<T>): List<V> {
        return when (val value = data[prop.name]) {
            is List<*> -> value.filterIsInstance<V>()
            else -> emptyList()
        }
    }
}

@Composable
fun <T : Any> YaForm(
    initial: T? = null,
    onSubmitScope: suspend (data: YaFormMap<T>) -> Unit,
    attrs: (AttrsScope<HTMLFormElement>.() -> Unit)? = null,
    content: @Composable YaFormScope<T>.() -> Unit,
) {
    val state = remember(initial) { YaFormState("yaform", initial) }
    val scope = rememberCoroutineScope()
    Form(attrs = {
        if (attrs != null) attrs()
        onSubmit { event ->
            val formElem = event.target as HTMLFormElement
            event.preventDefault()
            state.isLiveValidation = true
            if (!formElem.checkValidity()) {
                return@onSubmit
            }
            scope.launch {
                try {
                    state.isBusy = true
                    onSubmitScope(YaFormMap(state.data.toMap()))
                } catch (e: Exception) {
                    Toasts.display(Toast.error("Unerwarteter Fehler: ${e.message}"))
                } finally {
                    state.isBusy = false
                }
            }
        }
    }) {
        val scope = remember(this, state) { YaFormScope(this, state) }
        content(scope)
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormButton(
    type: ButtonType,
    attrs: AttrBuilderContext<HTMLButtonElement>? = null,
    disabled: Boolean = false,
    content: @Composable ElementScope<HTMLButtonElement>.() -> Unit,
) {
    Button(attrs = {
        this.type(type)
        attrs?.invoke(this)
        if (disabled) disabled()
        if (state.isBusy) ariaBusyAndDisabled()
    }) {
        content()
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormTextInput(
    prop: YaFormProperty<T>,
    type: InputType.InputTypeWithStringValue,
    label: @Composable () -> Unit,
    placeholder: String? = null,
    pattern: String? = null,
    required: Boolean = false,
    readonly: Boolean = false,
    disabled: Boolean = false,
) {
    val name = nameOf(prop)
    val value = stringValueOf(prop)

    Label {
        label()
        if (required) RequiredStar()
        Input(type, attrs = {
            name(name)
            if (placeholder != null) {
                placeholder(placeholder)
            } else if (type == InputType.Email) {
                placeholder("max.mustermann@mail.at")
            } else if (type == InputType.Password) {
                placeholder("••••••••••••")
            }
            if (pattern != null) pattern(pattern)
            if (required) required()
            if (readonly) readOnly()
            if (disabled || state.isBusy) disabled()
            value(value)
            onInput { event ->
                state.data[prop.name] = event.value
            }
        })
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormTextArea(
    prop: YaFormProperty<T>,
    rows: Int? = null,
    label: @Composable () -> Unit,
    placeholder: String? = null,
    required: Boolean = false,
) {
    val name = nameOf(prop)
    val value = stringValueOf(prop)

    Label {
        label()
        if (required) RequiredStar()
        TextArea(value) {
            name(name)
            if (rows != null) {
                rows(rows)
            }
            if (placeholder != null) {
                placeholder(placeholder)
            }
            if (required) required()
            if (state.isBusy) disabled()
            onInput { event ->
                val inputElem = event.target
                val newValue = inputElem.value
                state.data[prop.name] = newValue
            }
        }
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormLocalDateInput(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    placeholder: String? = null,
    min: LocalDate? = null,
    max: LocalDate? = null,
    required: Boolean = false,
    disabled: Boolean = false,
) {
    val name = nameOf(prop)
    val value = localDateValueOf(prop)

    Label {
        label()
        if (required) RequiredStar()
        Input(InputType.Date, attrs = {
            name(name)
            if (placeholder != null) placeholder(placeholder)
            if (min != null) {
                attr("min", min.toString())
            }
            if (max != null) {
                attr("max", max.toString())
            }
            if (required) required()
            if (disabled || state.isBusy) disabled()
            value(value)
            onInput { event ->
                val newValue = event.value
                state.data[prop.name] = newValue
            }
        })
    }
}

@Composable
fun <T : Any, E : Enum<E>> YaFormScope<T>.YaFormDropdown(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    options: List<Pair<E, String>>,
    required: Boolean = false,
    disabled: Boolean = false,
) {
    YaFormDropdown(
        prop = prop,
        label = label,
        options = options.map { (entry, label) -> PrefetchedEntry(entry.name, label) },
        required = required,
        disabled = disabled,
    )
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormDropdown(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    options: List<Pair<String, String>>,
    required: Boolean = false,
    disabled: Boolean = false,
) {
    YaFormDropdown(
        prop = prop,
        label = label,
        options = options.map { (id, label) -> PrefetchedEntry(id, label) },
        required = required,
        disabled = disabled,
    )
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormDropdown(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    options: List<PrefetchedEntry>,
    required: Boolean = false,
    disabled: Boolean = false,
) {
    val name = nameOf(prop)
    val value = stringValueOf(prop)

    Label {
        label()
        if (required) RequiredStar()
        Select(attrs = {
            name(name)
            if (required) required()
            if (disabled || state.isBusy) disabled()
            onChange { event ->
                val selectElem = event.target
                val newValue = selectElem.value
                state.data[prop.name] = newValue
            }
        }) {
            Option(value = "") { Text("Bitte wählen…") }
            options.forEach { entry ->
                key(entry.id) {
                    Option(value = entry.id, {
                        if (value == entry.id) {
                            selected()
                        }
                    }) {
                        Text(entry.label)
                    }
                }
            }
        }
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormNumberInput(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    placeholder: String? = null,
    min: Number? = null,
    max: Number? = null,
    required: Boolean = false,
    readonly: Boolean = false,
    disabled: Boolean = false,
) {
    val name = nameOf(prop)
    val value = numberValueOf(prop)

    Label {
        label()
        if (required) RequiredStar()
        NumberInput(value, min, max, attrs = {
            name(name)
            if (placeholder != null) placeholder(placeholder)
            if (required) required()
            if (readonly) readOnly()
            if (disabled || state.isBusy) disabled()
            onInput { event ->
                state.data[prop.name] = event.value ?: "error"
            }
        })
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormCheckInput(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    required: Boolean = false,
    disabled: Boolean = false,
) {
    val name = nameOf(prop)
    val value = booleanValueOf(prop)

    Label {
        CheckboxInput(value, attrs = {
            name(name)
            if (required) required()
            if (disabled || state.isBusy) disabled()
            onInput { event ->
                state.data[prop.name] = event.value
            }
        })
        label()
        if (required) RequiredStar()
    }
}

@Composable
fun <T : Any> YaFormScope<T>.YaFormStringListInput(
    prop: YaFormProperty<T>,
    label: @Composable () -> Unit,
    options: List<Pair<String, String>>,
) {
    val name = nameOf(prop)
    val selectedIds = listValueOf<String>(prop)

    Fieldset {
        Label {
            label()
        }
        options.forEach { (id, label) ->
            Label {
                CheckboxInput(
                    checked = selectedIds.contains(id),
                    attrs = {
                        name(name)
                        onInput {
                            val isChecked = it.value
                            val newList = if (isChecked) {
                                selectedIds + id
                            } else {
                                selectedIds - id
                            }
                            state.data[prop.name] = newList
                        }
                    }
                )
                Text(label)
            }
        }
    }
}

@Composable
fun RequiredStar() {
    Span(attrs = { classes("has-text-required") }) {
        Text(" *")
    }
}
